import { useEffect, useState } from "react";
import ReactLoading from "react-loading";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../../../../components/ConfirmationModal";
import { PageHeader } from "../../../../../components/Headers/PageHeader";
import { NotFound } from "../../../../../components/NotFound";
import { OrderStatus } from "../../../../../enums/Orders";
import { useAuth } from "../../../../../hooks/auth";
import { permissions } from "../../../../../shared/Permissions";
import { OrderDetailsComplete } from "../components/OrderDetailsComplete";
import { OrderDetailsError } from "../components/OrderDetailsError";
import * as S from "./styles";
import { useContextSelector } from "use-context-selector";
import { OrdersContext } from "../../../../../contexts/OrdersContext";
import { Modal } from "../../../../../components/New/Modal";
import { RefundModal } from "../components/RefundModal";
import { CancelModal } from "../components/CancelModal";
import { useMediaQuery } from "usehooks-ts";
import { DrawerComponent } from "../../../../../components/New/Drawer";
import { ResponsiveButtonContainer } from "../components/RefundModal/styles";
import { Button } from "../../../../../components/New/Button";
import { MarkOrderAsCanceledModal } from "../components/MarkOrderAsCanceledModal";

export function OrderDetails() {
  const { t } = useTranslation();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState<{
    type: "" | "refund" | "cancel" | "markAsCanceled" | "forceSync";
    isOpen: boolean;
  }>({
    type: "",
    isOpen: false,
  });

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [markAsCanceledModalOpen, setMarkAsCanceledModalOpen] = useState(false);

  const { orderId } = useParams();
  const { colors, responsiveDesign } = useTheme();

  const isResponsive = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);

  const {
    fetchOrderDetails,
    loading,
    ordersSyncErrorLoading,
    retryImport,
    orderDetails,
  } = useContextSelector(OrdersContext, (context) => {
    return context;
  });

  const { hasPermission } = useAuth();

  const navigate = useNavigate();

  const isFromEbay = orderDetails.sourceMarketplace === "Incycle Ebay";

  const isOrderMissing = !orderDetails.id;
  const isOrderLoading = loading.orderDetails;

  const refundButtonDisabled =
    isOrderMissing ||
    isOrderLoading ||
    orderDetails.permissions?.refundEnabled === false;

  const cancelButtonDisabled =
    isOrderMissing ||
    isOrderLoading ||
    orderDetails.permissions?.cancellationEnabled === false;

  const markAsCanceledEnabled =
    orderDetails.permissions?.markAsCanceledEnabled === true;

  const handleForceSync = async () => {
    try {
      await retryImport(orderDetails.id, true);
      await fetchOrderDetails(orderDetails.id);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails(orderId);
    }
  }, []);

  return (
    <S.OrdersDetailsContainer>
      <PageHeader
        title={`${t("orderId")}: #${orderDetails?.orderReferenceNumber}`}
        customBreadcrumbString={t("orderDetails")}
        customButton
        orderDetailsStatus={
          loading.orderDetails ? "loading" : orderDetails.processStatus
        }
      >
        <S.OrderAlignModalButtons>
          {isResponsive ? (
            <DrawerComponent
              open={
                filterDrawerOpen.isOpen && filterDrawerOpen.type === "refund"
              }
              onOpenChange={(open) => {
                setFilterDrawerOpen((prev) => ({
                  ...prev,
                  isOpen: open,
                }));
              }}
              triggerContent={
                <S.OrderButton
                  disabled={refundButtonDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterDrawerOpen({
                      type: "refund",
                      isOpen: true,
                    });
                  }}
                >
                  {isFromEbay ? t("refundItems") : t("refundOrder")}
                </S.OrderButton>
              }
              title={t("refundOrder")}
              showCloseButton
              showSeparator
            >
              <RefundModal
                sourceMarketplace={orderDetails.sourceMarketplace}
                sales={orderDetails.sales || []}
                setCloseDrawer={() =>
                  setFilterDrawerOpen({
                    type: "refund",
                    isOpen: false,
                  })
                }
                onSuccess={() => fetchOrderDetails(orderDetails.id)}
              />
            </DrawerComponent>
          ) : (
            <Modal
              title={t("refundOrder")}
              isOpen={refundModalOpen}
              onOpenChange={setRefundModalOpen}
              modalContent={
                <RefundModal
                  setCloseDrawer={() => setRefundModalOpen(false)}
                  sourceMarketplace={orderDetails.sourceMarketplace}
                  sales={orderDetails.sales || []}
                  onSuccess={() => fetchOrderDetails(orderDetails.id)}
                />
              }
              customButtons
              width="75rem"
            >
              <S.OrderButton disabled={refundButtonDisabled}>
                {isFromEbay ? t("refundItems") : t("refundOrder")}
              </S.OrderButton>
            </Modal>
          )}

          {markAsCanceledEnabled ? (
            isResponsive ? (
              <DrawerComponent
                open={
                  filterDrawerOpen.isOpen &&
                  filterDrawerOpen.type === "markAsCanceled"
                }
                onOpenChange={(open) => {
                  setFilterDrawerOpen((prev) => ({
                    ...prev,
                    isOpen: open,
                  }));
                }}
                triggerContent={
                  <S.OrderButton
                    disabled={isOrderMissing || isOrderLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      setFilterDrawerOpen({
                        type: "markAsCanceled",
                        isOpen: true,
                      });
                    }}
                  >
                    {t("markOrderAsCanceled")}
                  </S.OrderButton>
                }
                title={t("markOrderAsCanceled")}
                showCloseButton
                showSeparator
              >
                <MarkOrderAsCanceledModal
                  orderId={orderDetails.id}
                  onClose={() =>
                    setFilterDrawerOpen({
                      type: "markAsCanceled",
                      isOpen: false,
                    })
                  }
                  onSuccess={() => fetchOrderDetails(orderDetails.id)}
                />
              </DrawerComponent>
            ) : (
              <Modal
                title={t("markOrderAsCanceled")}
                isOpen={markAsCanceledModalOpen}
                onOpenChange={setMarkAsCanceledModalOpen}
                modalContent={
                  <MarkOrderAsCanceledModal
                    orderId={orderDetails.id}
                    onClose={() => setMarkAsCanceledModalOpen(false)}
                    onSuccess={() => fetchOrderDetails(orderDetails.id)}
                  />
                }
                customButtons
              >
                <S.OrderButton disabled={isOrderMissing || isOrderLoading}>
                  {t("markOrderAsCanceled")}
                </S.OrderButton>
              </Modal>
            )
          ) : isResponsive ? (
            <DrawerComponent
              open={
                filterDrawerOpen.isOpen && filterDrawerOpen.type === "cancel"
              }
              onOpenChange={(open) => {
                setFilterDrawerOpen((prev) => ({
                  ...prev,
                  isOpen: open,
                }));
              }}
              triggerContent={
                <S.OrderButton
                  disabled={cancelButtonDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterDrawerOpen({
                      type: "cancel",
                      isOpen: true,
                    });
                  }}
                >
                  {t("cancelOrder")}
                </S.OrderButton>
              }
              title={t("cancelOrder")}
              showCloseButton
              showSeparator
            >
              <CancelModal
                order={{
                  orderId: orderDetails.id,
                  sourceMarketplace: orderDetails.sourceMarketplace,
                  sales: orderDetails.sales || [],
                }}
                setCloseDrawer={() =>
                  setFilterDrawerOpen({
                    type: "cancel",
                    isOpen: false,
                  })
                }
                onSuccess={() => fetchOrderDetails(orderDetails.id)}
              />
            </DrawerComponent>
          ) : (
            <Modal
              title={t("cancelOrder")}
              isOpen={cancelModalOpen}
              onOpenChange={setCancelModalOpen}
              modalContent={
                <CancelModal
                  setCloseDrawer={() => setCancelModalOpen(false)}
                  order={{
                    orderId: orderDetails.id,
                    sourceMarketplace: orderDetails.sourceMarketplace,
                    sales: orderDetails.sales || [],
                  }}
                  onSuccess={() => fetchOrderDetails(orderDetails.id)}
                />
              }
              customButtons
            >
              <S.OrderButton disabled={cancelButtonDisabled}>
                {t("cancelOrder")}
              </S.OrderButton>
            </Modal>
          )}

          {isResponsive ? (
            <DrawerComponent
              open={
                filterDrawerOpen.isOpen && filterDrawerOpen.type === "forceSync"
              }
              onOpenChange={(open) => {
                setFilterDrawerOpen((prev) => ({
                  ...prev,
                  isOpen: open,
                }));
              }}
              triggerContent={
                <S.OrderButton
                  disabled={refundButtonDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterDrawerOpen({
                      type: "forceSync",
                      isOpen: true,
                    });
                  }}
                >
                  {t("forceSyncAll")}
                </S.OrderButton>
              }
              title={t("confirmForceSyncTitle")}
              description={t("confirmForceSyncDescription")}
            >
              <ResponsiveButtonContainer style={{ margin: 0 }}>
                <Button
                  isLoading={ordersSyncErrorLoading.loading}
                  onClick={handleForceSync}
                  title={t("forceSyncAll")}
                  variant="destructive"
                />
                <Button
                  variant="ghost"
                  title={t("cancel")}
                  isLoading={ordersSyncErrorLoading.loading}
                  onClick={(e) => {
                    e.preventDefault();
                    setFilterDrawerOpen({
                      type: "forceSync",
                      isOpen: false,
                    });
                  }}
                />
              </ResponsiveButtonContainer>
            </DrawerComponent>
          ) : (
            <ConfirmationModal
              title={t("confirmForceSyncTitle")}
              description={t("confirmForceSyncDescription")}
              variant="delete"
              confirmFunction={async () => handleForceSync()}
            >
              <S.OrderButton
                disabled={
                  ordersSyncErrorLoading.loading ||
                  orderDetails.forceReprocess ||
                  !hasPermission(permissions.report.orders.edit) ||
                  orderDetails.processStatus !== OrderStatus.SUCCESS
                }
              >
                {ordersSyncErrorLoading.loading ? (
                  <ReactLoading
                    height={24}
                    width={24}
                    type={"spin"}
                    color="#fff"
                  />
                ) : (
                  t("forceSyncAll")
                )}
              </S.OrderButton>
            </ConfirmationModal>
          )}
        </S.OrderAlignModalButtons>
      </PageHeader>

      {loading.orderDetails ? (
        <S.LoadingContainer>
          <ReactLoading
            type="spin"
            height={60}
            width={60}
            color={colors["gray-800"]}
          />
        </S.LoadingContainer>
      ) : orderDetails.id ? (
        orderDetails?.sales?.length ? (
          <OrderDetailsComplete />
        ) : (
          <OrderDetailsError />
        )
      ) : (
        !loading.orderDetails && (
          <NotFound
            content={t("orderError")}
            buttonTitle={t("orders")}
            onClick={() => navigate("/report/orders")}
          />
        )
      )}
    </S.OrdersDetailsContainer>
  );
}
